import { Schema } from "effect";

/**
 * Describes the range of an integer query parameter on its encoded string. v4 leaves checks on the
 * decoded number out of the generated parameter schema, which would otherwise be a bare string;
 * v3 described the bounds there.
 */
export const describeIntRange = <S extends Schema.Top>(range: {
  readonly minimum: number;
  readonly maximum?: number;
}) =>
  Schema.annotateEncoded<S>({
    description:
      range.maximum === undefined
        ? `an integer greater than or equal to ${String(range.minimum)}`
        : `an integer between ${String(range.minimum)} and ${String(range.maximum)}`,
  });

type JsonObject = Record<string, unknown>;

const isJsonObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isNullSchema = (schema: unknown): boolean =>
  isJsonObject(schema) && schema["type"] === "null";

const withoutNullBranch = (schema: unknown): unknown => {
  if (!isJsonObject(schema)) return schema;
  const { anyOf, ...rest } = schema;
  if (!Array.isArray(anyOf) || !anyOf.some(isNullSchema)) return schema;
  const branches = anyOf.filter((branch) => !isNullSchema(branch));
  return branches.length === 1 && isJsonObject(branches[0])
    ? { ...branches[0], ...rest }
    : { ...rest, anyOf: branches };
};

const withNonNullQueryParameters = (item: unknown): unknown => {
  if (!isJsonObject(item)) return item;
  const parameters = item["parameters"];
  if (!Array.isArray(parameters)) return item;
  return {
    ...item,
    parameters: parameters.map((parameter: unknown) =>
      isJsonObject(parameter) && parameter["in"] === "query"
        ? { ...parameter, schema: withoutNullBranch(parameter["schema"]) }
        : parameter
    ),
  };
};

const mapValues = (record: JsonObject, f: (value: unknown) => unknown): JsonObject =>
  Object.fromEntries(Object.entries(record).map(([key, value]) => [key, f(value)]));

/**
 * `OpenApi.Transform` for an API whose optional query keys accept `undefined`. v4 renders such a
 * key as `anyOf: [X, { type: "null" }]`, but a query string value is never null, so the parameter
 * schema is X, as in v3. Covers both path-level and operation-level parameters.
 */
export const withoutNullQueryParameters = (
  spec: Record<string, unknown>
): Record<string, unknown> => {
  const paths: unknown = spec["paths"];
  if (!isJsonObject(paths)) return spec;
  return {
    ...spec,
    paths: mapValues(paths, (item) => {
      const pathItem = withNonNullQueryParameters(item);
      return isJsonObject(pathItem) ? mapValues(pathItem, withNonNullQueryParameters) : pathItem;
    }),
  };
};
